import React from "react";
import { connect } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";

import ConditionalButton from "../../../layout/conditional-button";
import { loading } from "../../../actions/page";
import { periods as getPayPeriods, get as getTimecards, clockIn, clockOut, startBreak, endBreak, save as saveTimecard } from "../../../actions/timecards";
import { withNavigation } from "../../../utils/wrapper";
import { minutesToHM, format, setFormattedHours, formatMinutesToHours, formatShortDate, formatTime, sameDay, valid } from "../../../utils/date";
import { breakActionFor, clockActionFor, hasOpenTimecard } from "./state";


// Optional (if you’re using it elsewhere in this area)
// import CsLineIcons from "../../../cs-line-icons";

const STATUS_VALUES = ["all", "draft", "submitted", "approved", "rejected", "locked", "void"];
const BREAK_HOUR_OPTIONS = Array.from({ length: 13 }).map((_, i) => ({ value: i, label: `${i}h` }));
const BREAK_MINUTE_OPTIONS = Array.from({ length: 12 }).map((_, i) => {
  const value = i * 5;
  return { value, label: `${String(value).padStart(2, "0")}m` };
});


const DateCardInput = React.forwardRef(
  ({ onClick, monthLabel, dayNumber }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className="border rounded p-2 text-center bg-white w-100"
      style={{ cursor: "pointer" }}
    >
      <div className="text-muted text-small text-uppercase">{monthLabel}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: "30px" }}>{dayNumber}</div>
    </button>
  )
);

class TimeSheetTracking extends React.Component {
    constructor(props) {
        super(props);
        this.props.loading(true);

        this.state = {
            status: "all",
            filterDate: null,
            previousDate: null,
            payperiod: null,
            activeDate: new Date(),
            timecards: [],
            // inline edit state (header)
            editing: {
                field: null,
                value: ""
            },

            break: {
                editing: false,
                hours: 0,
                minutes: 0,
                total: null,
            },
            // current session
            clock: {
                in: null,
                out: null
            },

            active: {
                uuid: null,
                startsOn: null,
                endsOn: null,
                break: 0,
                breakStartedAt: null,
                lockedAt: null,
            },

            // rows
            rows: [
                // { date: "Nov 21, 2023", checkIn: "9:00 AM", checkOut: "5:00 PM", break: "1:00 hr", workingHours: "8:00 hrs" },
            ],
        };
    }

    componentDidMount = () => {
        setTimeout(async () => {
            if (!this.state.activeDate) this.setState({ activeDate: new Date() });
            await this.loadData();
        }, 30);
    };

    loadData = async () => {
        this.props.loading(true);
        try {
            const payperiods = await this.props.getPayPeriods({ dateOf: this.state.activeDate });
            const payperiod = !!payperiods?.items?.[0] ? payperiods.items[0] : null;

            if (!payperiod) {
                this.setState((prev) => ({
                    payperiod: null,
                    timecards: [],
                    rows: [],
                    active: {
                        uuid: null,
                        startsOn: null,
                        endsOn: null,
                        break: 0,
                        breakStartedAt: null,
                        lockedAt: null,
                    },
                    clock: {
                        in: null,
                        out: null,
                    },
                    break: {
                        ...prev.break,
                        total: "0:00 hr",
                    },
                    activeDate: prev.previousDate || prev.activeDate || new Date(),
                }));
                return;
            };

            const slice = await this.props.getTimecards({
                staffUuid: this.props.owner,
                payPeriodUuid: payperiod.uuid
            });

            const list = Array.isArray(slice.items) ? slice.items : [];

            const header = this.deriveHeaderFromSlice({ slice: list, activeDate: this.state.activeDate });

            this.setState(prev => ({
                timecards: list,
                payperiod,
                rows: this.buildRowsFromList(list, {
                    status: this.state.status || "all",
                    filterDate: this.state.filterDate || null,
                }),
                active: {
                    ...prev.active,
                    uuid: header?.uuid ?? null,
                    startsOn: header?.startsOn ?? null,
                    endsOn: header?.endsOn ?? null,
                    break: Number(header?.break ?? 0) || 0,
                    breakStartedAt: header?.breakStartedAt ?? null,
                    lockedAt: header?.lockedAt ?? null,
                },
                clock: {
                    ...prev.clock,
                    in: formatTime({ date: header?.startsOn }),
                    out: formatTime({ date: header?.endsOn }),
                },
                break: {
                    ...prev.break,
                    total: formatMinutesToHours(header?.break),
                }
            }));

        } finally {
            this.props.loading(false);
        }
    };

    canCreateOrEdit = () => {
        // editing is allowed if payperiod exists and is not locked (and if an active timecard is locked, that's handled by isEditingLocked)
        if (!this.state?.payperiod?.uuid) return false;
        if (valid(this.state?.payperiod?.lockedAt)) return false;
        // if there IS an active timecard and it's locked, block
        if (valid(this.state?.active?.lockedAt)) return false;
        return true;
    };

    canEditExisting = () => {
        return this.canCreateOrEdit() && Boolean(this.state?.active?.uuid);
    };

    startEditBreaks = () => {
        if (!this.canEditExisting()) return;

        const { h, m } = minutesToHM(Number(this.state.active?.break ?? 0) || 0);
        this.setState(prev => ({
            break: { ...prev.break, editing: true, hours: h, minutes: m }
        }));
    };

    onBreakHoursChange = (option) => {
        this.setState(prev =>
        (
            {
                break: {
                    ...prev.break,
                    hours: parseInt(option?.value, 10) || 0
                }
            }
        ));
    };

    onBreakMinutesChange = (option) => {
        this.setState(prev =>
        (
            {
                break: {
                    ...prev.break,
                    minutes: parseInt(option?.value, 10) || 0
                }
            }
        ));
    };

    commitBreakEdit = async () => {
        if (this.isEditingLocked()) return;

        const active = this.state.active;
        const breakState = this.state.break;

        this.setState((prev) => ({
            break: {
                ...prev.break,
                editing: false,
            },
        }));

        if (!active?.uuid) return;

        const totalMinutes =
            (Math.max(0, breakState.hours) * 60) + Math.max(0, breakState.minutes);

        const updated = await this.props.saveTimecard({
            uuid: active.uuid,
            staffUuid: this.props.owner,
            break: totalMinutes,
        });

        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };

    cancelBreakEdit = () => {
        this.setState(prev => (
            {
                break: {
                    ...prev.break,
                    editing: false
                }
            }));
    };

    onBreakKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            this.commitBreakEdit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            this.cancelBreakEdit();
        }
    };


    upsertTimecardInState = (updated) => {
        if (!updated?.uuid) return;

        this.setState((prev) => {
            const list = Array.isArray(prev.timecards) ? prev.timecards.slice() : [];
            const idx = list.findIndex((x) => x?.uuid === updated.uuid);

            if (idx >= 0) list[idx] = { ...list[idx], ...updated };
            else list.unshift(updated); // or push, depending on your UX

            const active = this.deriveHeaderFromSlice({ slice: list, activeDate: prev.activeDate });

            const rows = this.buildRowsFromList(list, {
                status: prev.status || "all",
                filterDate: prev.filterDate || null,
            });

            return {
                timecards: list,
                rows,
                break: {
                    ...prev.break,
                    total: formatMinutesToHours(active?.break)
                },
                active: {
                    ...prev.active,
                    uuid: active?.uuid ?? null,
                    startsOn: active?.startsOn ?? null,
                    endsOn: active?.endsOn ?? null,
                    break: Number(active?.break ?? 0) || 0,
                    breakStartedAt: active?.breakStartedAt ?? null,
                    lockedAt: active?.lockedAt ?? null,
                },
                clock: {
                    ...prev.clock,
                    in: formatTime({date: active?.startsOn}),
                    out: formatTime({date: active?.endsOn}),
                },

            };
        });
    };

    buildRowsFromList = (list, { status = "all", filterDate = null } = {}) => {
        let filtered = Array.isArray(list) ? [...list] : [];

        if (status !== "all") {
            filtered = filtered.filter(
                (item) => String(item?.status || "").toLowerCase() === String(status).toLowerCase()
            );
        }

        if (filterDate) {
            filtered = filtered.filter((item) => sameDay(item?.startsOn, filterDate));
        }

        const sorted = filtered.sort((a, b) => {
            const ad = new Date(a?.startsOn || 0).getTime();
            const bd = new Date(b?.startsOn || 0).getTime();
            return bd - ad;
        });

        return sorted.map((item) => ({
            uuid: item.uuid,
            startsOn: item.startsOn,
            date: formatShortDate(item.startsOn),
            checkIn: formatTime({ date: item.startsOn }),
            checkOut: formatTime({ date: item.endsOn }),
            break: formatMinutesToHours(item.break),
            workingHours: formatMinutesToHours(item.minutesWorked),
        }));
    };

    applyFiltersAndSetRows = () => {
        const rows = this.buildRowsFromList(this.state.timecards, {
            status: this.state.status || "all",
            filterDate: this.state.filterDate || null,
        });
        this.setState({ rows });
    };


    formatMonthUpper = (d) => {
        try {
            return d.toLocaleString("en-US", { month: "short" }).toUpperCase();
        } catch {
            return "—";
        }
    };

    isActiveDateToday = () => {
        // if activeDate is null, treat as today (your default behavior)
        const active = this.state.activeDate || new Date();
        return sameDay(active, new Date());
    };

    isEditingLocked = () => {
        let _locked = Boolean(valid(this.state?.payperiod?.lockedAt));
        _locked |= Boolean(valid(this.state?.active?.lockedAt));
        return _locked;
    };

    deriveHeaderFromSlice = ({ slice, activeDate }) => {
        const list = Array.isArray(slice) ? slice : [];
        const day = activeDate || new Date();

        const dayCards = list.filter(tc => sameDay(tc?.startsOn, day));

        if (!dayCards.length) {
            return null;
        }

        const open = dayCards
            .filter(tc => tc?.startsOn && !tc?.endsOn)
            .sort((a, b) => new Date(b.startsOn).getTime() - new Date(a.startsOn).getTime())[0] || null;

        const latest = !open
            ? dayCards.slice().sort((a, b) => {
                const ad = new Date(a?.endsOn || a?.startsOn || 0).getTime();
                const bd = new Date(b?.endsOn || b?.startsOn || 0).getTime();
                return bd - ad;
            })[0]
            : null;

        return open || latest;
    };


    // ---- react-select (Status)
    get statusOptions() {
        return STATUS_VALUES.map((s) => ({
            value: s,
            label: s === "all" ? "Status" : s.toUpperCase(),
        }));
    }

    get selectedStatusOption() {
        const v = this.state.status || "all";
        return this.statusOptions.find((o) => o.value === v) || this.statusOptions[0];
    }

    startEditHeaderTime = (field) => {
        // Allow editing startsOn even if there is no active.uuid (create case)
        if (!this.canCreateOrEdit()) return;

        // For endsOn, require an existing timecard (or at least a startsOn already)
        if (field === "endsOn" && !this.state?.active?.uuid) return;

        const raw =
            field === "startsOn" ? this.state.active?.startsOn : this.state.active?.endsOn;

        this.setState(prev => ({
            editing: {
                ...prev.editing,
                field,
                value: format(raw, true, true) || "", // ok if null => ""
            }
        }));
    };

    onHeaderTimeChange = (e) => {
        this.setState(prev => (
            {
                editing: {
                    ...prev.editing,
                    value: e.target.value,
                }
            }
        ));
    };

    commitHeaderTimeEdit = async () => {
        const { field, value } = this.state.editing;
        const activeUuid = this.state.active?.uuid;

        if (!field) return;

        // always exit edit mode
        this.setState(prev => ({
            editing: { ...prev.editing, field: null }
        }));

        if (!this.canCreateOrEdit()) return;
        if (!value) return;

        const dt = setFormattedHours(this.state.activeDate || new Date(), value);
        if (!dt) return;

        // If no uuid and trying to set endsOn, ignore (force user to set startsOn first)
        if (!activeUuid && field === "endsOn") return;

        const payload = {
            staffUuid: this.props.owner,
            payPeriodUuid: this.state.payperiod.uuid,
            [field]: dt.toISOString(),
        };

        // If we already have a timecard, update it. If not, create one.
        if (activeUuid) payload.uuid = activeUuid;

        const updated = await this.props.saveTimecard(payload);

        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };


    cancelHeaderTimeEdit = () => {
        this.setState(prev => (
            {
                editing: {
                    ...prev.editing,
                    field: null,
                    value: "",
                }
            }
        ));
    };

    onHeaderTimeKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            this.commitHeaderTimeEdit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            this.cancelHeaderTimeEdit();
        }
    };


    onStatusChange = (opt) => {
        const value = opt?.value ?? "all";
        this.setState({ status: value }, this.applyFiltersAndSetRows);
    };

    onDateChange = (date) => {
        this.setState({ filterDate: date }, this.applyFiltersAndSetRows);
    };

    // ---- actions
    onStartBreak = async () => {
        const uuid = this.state.active?.uuid;
        if (!uuid || !hasOpenTimecard(this.state.active)) return;
        const updated = await this.props.startBreak({ uuid, atTime: new Date() });
        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };

    onEndBreak = async () => {
        const uuid = this.state.active?.uuid;
        if (!uuid || !hasOpenTimecard(this.state.active)) return;
        const updated = await this.props.endBreak({ uuid, atTime: new Date() });
        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };
    
    onClockOut = async () => {
        const uuid = this.state.active?.uuid;
        if (!uuid) return;

        const updated = await this.props.clockOut({
            staffUuid: this.props.owner,
            atTime: new Date(),
            uuid
        });

        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };

    onClockIn = async () => {
        if (!this.state.payperiod?.uuid || hasOpenTimecard(this.state.active)) return;
        const updated = await this.props.clockIn({
            staffUuid: this.props.owner,
            atTime: new Date(),
            payPeriodUuid: this.state.payperiod.uuid
        });

        // If the action returns `{ item }` or `{ data }`, adjust here:
        this.upsertTimecardInState(updated?.item ?? updated?.data ?? updated);
    };

    
    onActiveDateChange = (date) => {
        const next = date || new Date();
        this.setState(
            (prev) => ({
                activeDate: next,
                previousDate: prev.activeDate,
                filterDate: null,
            }),
            this.loadData
        );
    };

    // Update renderClockActionButton to ONLY show actions if activeDate === today

    renderClockActionButton() {
        // Only allow clock in/out on today
        if (!this.isActiveDateToday() || !this.state.payperiod) return null;
        if (valid(this.state.payperiod.lockedAt)) return null;

        const action = clockActionFor(this.state.active);
        if (action === "out" && valid(this.state.active?.lockedAt)) return null;

        if (action === "in") {
            return (
                <ConditionalButton
                    enabledVariant="success"
                    tooltip="Clock In"
                    display={true}
                    enabled={true}
                    onEnabledClick={this.onClockIn}
                    icon="login"
                >
                    Clock In
                </ConditionalButton>
            );
        }

        if (action === "out") {
            return (
                <ConditionalButton
                    enabledVariant="danger"
                    tooltip="Clock Out"
                    display={true}
                    enabled={true}
                    onEnabledClick={this.onClockOut}
                    icon="logout"
                >
                    Clock Out
                </ConditionalButton>
            );
        }

        return null;
    }


    // Update renderStartBreakButton similarly (since it’s also a “current-day” action)

    renderStartBreakButton() {
        if (!this.isActiveDateToday() || !this.state.payperiod) return null;
        if (valid(this.state.payperiod.lockedAt) || valid(this.state.active?.lockedAt)) return null;
        const action = breakActionFor(this.state.active);
        if (!action) return null;
        const ending = action === "end";

        return (
            <ConditionalButton
                enabledVariant={ending ? "primary" : "outline-primary"}
                tooltip={ending ? "End Break" : "Start Break"}
                display={true}
                enabled={true}
                onEnabledClick={ending ? this.onEndBreak : this.onStartBreak}
                icon="cup"
            >
                {ending ? "End Break" : "Start Break"}
            </ConditionalButton>
        );
    }



    renderHeaderCard() {
        const { activeDate, active: _active } = this.state;
        const monthLabel = this.formatMonthUpper(activeDate || new Date());
        const dayNumber = (activeDate || new Date()).getDate();
        const locked = this.isEditingLocked();
        const canSetClockIn = this.canCreateOrEdit();     // allows click when N/A
        const canEditClockOut = this.canEditExisting();   // requires a timecard
        const canEditBreaks = this.canEditExisting();

        return (
            <div className="p-3 border rounded mb-3">
                <Row className="align-items-center g-3">
                    <Col xs="12" md="2" xl="1">
                        <DatePicker
                            selected={activeDate || new Date()}
                            onChange={this.onActiveDateChange}
                            isClearable
                            dateFormat="MMM d, yyyy"
                            popperPlacement="bottom-start"
                            customInput={<DateCardInput monthLabel={monthLabel} dayNumber={dayNumber} />}
                        />
                    </Col>

                    <Col xs="12" md="6" xl="5" className="d-flex gap-2">
                        {this.renderStartBreakButton()}
                        {this.renderClockActionButton()}
                    </Col>

                    <Col xs="12" md="4" xl="6">
                        <Row className="g-0">
                            <Col xs="4" className="pe-3 border-end">
                                <div className="text-muted text-small">Clock In</div>

                                {this.state.editing.field === "startsOn" ? (
                                    <Form.Control
                                        type="time"
                                        autoFocus
                                        value={this.state.editing.value}
                                        onChange={this.onHeaderTimeChange}
                                        onBlur={this.commitHeaderTimeEdit}
                                        onKeyDown={this.onHeaderTimeKeyDown}
                                        size="sm"
                                    />
                                ) : (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 700,
                                            cursor: canSetClockIn ? "pointer" : "not-allowed",
                                            opacity: canSetClockIn ? 1 : 0.6,
                                        }}
                                        onClick={() => canSetClockIn && this.startEditHeaderTime("startsOn")}
                                        onKeyDown={(e) => {
                                            if (!canSetClockIn) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                this.startEditHeaderTime("startsOn");
                                            }
                                        }}
                                        title={canSetClockIn ? "Click to edit" : "Locked / No pay period"}
                                    >
                                        {this.state.clock.in?.trim?.() ? this.state.clock.in : "N/A"}
                                    </div>
                                )}
                            </Col>

                            <Col xs="4" className="ps-3 border-end">
                                <div className="text-muted text-small">Breaks</div>

                                {!_active?.uuid ? (
                                    <div style={{ fontSize: 24, fontWeight: 700 }}>N/A</div>
                                ) : this.state.break.editing ? (
                                    <div
                                        className="d-flex align-items-center gap-2"
                                        tabIndex={0}
                                        onKeyDown={this.onBreakKeyDown}
                                        onBlur={(e) => {
                                            // commit only if focus left this container (not just moved between selects)
                                            if (!e.currentTarget.contains(e.relatedTarget)) this.commitBreakEdit();
                                        }}
                                    >
                                        <Select
                                            inputId="breakHours"
                                            classNamePrefix="react-select"
                                            value={BREAK_HOUR_OPTIONS.find((option) => option.value === this.state.break.hours)}
                                            onChange={this.onBreakHoursChange}
                                            isDisabled={locked}
                                            isClearable={false}
                                            options={BREAK_HOUR_OPTIONS}
                                        />

                                        <Select
                                            inputId="breakMinutes"
                                            classNamePrefix="react-select"
                                            value={BREAK_MINUTE_OPTIONS.find((option) => option.value === this.state.break.minutes)}
                                            onChange={this.onBreakMinutesChange}
                                            isDisabled={locked}
                                            isClearable={false}
                                            options={BREAK_MINUTE_OPTIONS}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 700,
                                            cursor: canEditBreaks ? "pointer" : "not-allowed",
                                            opacity: canEditBreaks ? 1 : 0.6,
                                        }}
                                        onClick={() => canEditBreaks && this.startEditBreaks()}
                                        onKeyDown={(e) => {
                                            if (!canEditBreaks) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                this.startEditBreaks();
                                            }
                                        }}
                                        title={canEditBreaks ? "Click to edit" : "Locked / No timecard"}
                                    >
                                        {this.state.break.total?.trim?.() ? this.state.break.total : "0:00 hr"}
                                    </div>
                                )}
                            </Col>

                            <Col xs="4" className="ps-3">
                                <div className="text-muted text-small">Clock Out</div>

                                {this.state.editing.field === "endsOn" ? (
                                    <Form.Control
                                        type="time"
                                        autoFocus
                                        value={this.state.editing.value}
                                        onChange={this.onHeaderTimeChange}
                                        onBlur={this.commitHeaderTimeEdit}
                                        onKeyDown={this.onHeaderTimeKeyDown}
                                        size="sm"
                                    />
                                ) : (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 700,
                                            cursor: canEditClockOut ? "pointer" : "not-allowed",
                                            opacity: canEditClockOut ? 1 : 0.6,
                                        }}
                                        onClick={() => canEditClockOut && this.startEditHeaderTime("endsOn")}
                                        onKeyDown={(e) => {
                                            if (!canEditClockOut) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                this.startEditHeaderTime("endsOn");
                                            }
                                        }}
                                        title={canEditClockOut ? "Click to edit" : "Locked / No timecard"}
                                    >
                                        {this.state.clock.out?.trim?.() ? this.state.clock.out : "N/A"}
                                    </div>
                                )}
                            </Col>

                        </Row>
                    </Col>
                </Row>
            </div>
        );
    }

    renderTable() {
        const rows = this.state.rows || [];

        return (
            <div className="border rounded overflow-hidden">
                <table className="table mb-0">
                    <thead style={{ background: "#f7f7f9" }}>
                        <tr className="text-muted text-small text-uppercase">
                            <th className="ps-3" style={{ width: "35%" }}>Date</th>
                            <th style={{ width: "15%" }}>Clock In</th>
                            <th style={{ width: "15%" }}>Clock Out</th>
                            <th style={{ width: "15%" }}>Break</th>
                            <th className="pe-3 text-end" style={{ width: "20%" }}>Working Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => (
                            <tr key={`ts_row_${idx}`}>
                                <td className="ps-3">{r.date}</td>
                                <td>{r.checkIn}</td>
                                <td>{r.checkOut}</td>
                                <td>{r.break}</td>
                                <td className="pe-3 text-end">{r.workingHours}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td className="p-4 text-muted" colSpan={5}>
                                    No entries found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        return (
            <>
                <Col xs="12">
                    {this.renderHeaderCard()}
                    {this.renderTable()}
                </Col>
            </>
        );
    }
}

TimeSheetTracking.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default withNavigation(connect(mapStateToProps, { loading, getPayPeriods, getTimecards, clockIn, clockOut, startBreak, endBreak, saveTimecard })(TimeSheetTracking));
